import { Dispatch, SetStateAction, useCallback } from 'react';
import BigNumber from 'bignumber.js';
import * as Starknet from 'starknet';
import { hexToBigInt, parseGwei } from 'viem';

import L1_ERC20_BRIDGE_ABI from '#/features/abis/L1_ERC20_BRIDGE_ABI';
import {
  DepositState,
  useDepositState,
} from '#/features/deposit/deposit-context';
import { useDepositFormState } from '#/features/deposit/form-context';
import EthereumWallet, {
  EthereumAddress,
} from '#/features/ethereum-wallet/wallet';
import { logException } from '#/features/logging/logging';
import { BridgedToken } from '#/features/paraclear';
import {
  StarknetProvider,
  useStarknetProvider,
} from '#/features/starknet-wallet/provider';
import { prepareErrorMessage } from '#/features/wallet/ethereum';
import { useWalletState } from '#/features/wallet/wallet-context';

import { toQuantums } from '#/utils/quantums';
import { AsyncResult } from '#/utils/types';

interface DepositToEthereumBridge {
  (token: BridgedToken): AsyncResult<void>;
}

export default function useDepositToEthereumBridge(): DepositToEthereumBridge {
  const [walletState] = useWalletState();
  const [depositState, setDepositState] = useDepositState();
  const [formState] = useDepositFormState();
  const starknetProvider = useStarknetProvider();

  return useCallback(
    async function depositToEthereumBridge(token: BridgedToken) {
      if (walletState.ethereumAddress == null) {
        throw new Error(
          'depositToEthereumBridge called before ethereumAddress available',
        );
      }
      if (walletState.starknetAddress == null) {
        throw new Error(
          'depositToEthereumBridge called before starknetAddress available',
        );
      }
      if (formState.amount == null) {
        throw new Error(`deposit amount is not defined`);
      }

      if (depositState.status !== 'idle') {
        throw new Error(
          `Deposit is already in progress (status is ${depositState.status}).`,
        );
      }

      const actions = prepareActions(setDepositState);

      actions.resetError();

      try {
        actions.setStatus('busy');
        await requestDepositToEthereumBridge(
          formState.amount,
          walletState.ethereumAddress,
          walletState.starknetAddress,
          token,
          starknetProvider,
        );
      } catch (err) {
        const description = 'Failed to deposit funds to Starknet';
        const { message, isException } = prepareErrorMessage(description, err);
        const error = new Error(description, { cause: err });
        if (isException) logException(error);
        actions.setError(message);
        actions.setStatus('idle');
        return { ok: false, error, message: description };
      }

      actions.setStatus('idle');
      return { ok: true, data: undefined };
    },
    [
      depositState.status,
      formState.amount,
      setDepositState,
      starknetProvider,
      walletState.ethereumAddress,
      walletState.starknetAddress,
    ],
  );
}

const DEFAULT_FEE_WEI = parseGwei('2000');
async function requestDepositToEthereumBridge(
  amountBn: BigNumber,
  ethereumAddress: EthereumAddress,
  starknetAddress: string,
  token: BridgedToken,
  starknetProvider: StarknetProvider,
) {
  const amount = toQuantums(amountBn, token.decimals);
  const amountUint256 = Starknet.uint256.bnToUint256(amount);
  const recipient = starknetAddress as `0x${string}`;

  let feeEstimate;
  try {
    feeEstimate = await starknetProvider.estimateMessageFee({
      from_address: token.l1BridgeAddress,
      to_address: token.l2BridgeAddress,
      entry_point_selector: 'handle_deposit',
      payload: [
        recipient,
        amountUint256.low.toString(),
        amountUint256.high.toString(),
      ],
    });
  } catch (cause) {
    const message = `Falling back to the default fee of WEI='${DEFAULT_FEE_WEI}'. Failed to estimate message fee for deposit to Ethereum Bridge.`;
    logException(new Error(message, { cause }));
    feeEstimate = { overall_fee: DEFAULT_FEE_WEI };
  }

  const { request } = await EthereumWallet.prepareWriteContract({
    address: token.l1BridgeAddress,
    abi: L1_ERC20_BRIDGE_ABI,
    functionName: 'deposit',
    args: [BigInt(amount), hexToBigInt(recipient)],
    value: BigInt(feeEstimate.overall_fee),
    /* the account to send the transaction from. */
    account: ethereumAddress,
  });

  return EthereumWallet.writeContract(request);
}

function prepareActions(setState: Dispatch<SetStateAction<DepositState>>) {
  function setStatus(status: DepositState['status']) {
    setState((state) => ({ ...state, status }));
  }

  const setError = (error: string) => {
    setState((state) => ({ ...state, error }));
  };

  const resetError = () => {
    setState((state) => ({ ...state, error: '' }));
  };

  return { setStatus, setError, resetError };
}
